import { ReactNode, Suspense } from "react";

import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    PageNavMenu,
    PageNavMenuItem,
    PageNavMenuSection,
    PageSpinner,
} from "@impero-com/ui";

import { PageNavMenuLayout, PageNavMenuMain } from "../../components/layout";
import { WebRoute } from "../../routes";
import { useAvailableActions } from "../../utils/hooks/useAvailableActions";
import { useFeatureFlag } from "../../utils/hooks/useFeatureFlag";
import { controlEntitiesListUrl } from "../controlEntities/urlUtils";

export type SettingsSection =
    | "profile"
    | "tags"
    | "entities"
    | "eventLog"
    | "controlPolicies";

interface SettingsLayoutProps {
    section: SettingsSection;
    navigation?: ReactNode;
    actions?: ReactNode;
    children: ReactNode;
}

/**
 * New menu only: on the old menu, the Settings pages keep their own
 * application bar and no Settings menu.
 */
export function SettingsLayout({
    section,
    navigation,
    actions,
    children,
}: SettingsLayoutProps) {
    return (
        <PageNavMenuLayout
            title={{
                title: sectionLabel(section),
                breadCrumbs: [
                    { label: t("Navigation_Settings"), url: WebRoute.PROFILE },
                ],
            }}
            routerLinks
            menu={<SettingsMenu section={section} />}
        >
            <PageNavMenuMain navigation={navigation} actions={actions}>
                <Suspense fallback={<PageSpinner />}>{children}</Suspense>
            </PageNavMenuMain>
        </PageNavMenuLayout>
    );
}

interface SettingsMenuProps {
    section: SettingsSection;
}

function SettingsMenu({ section }: SettingsMenuProps) {
    const controlEntitiesEnabled = useFeatureFlag("controlEntities");
    const orgSettingsEnabled = useFeatureFlag("orgSettings");
    const canManageControlPolicies =
        useAvailableActions().organization.controlPolicies.update;

    return (
        <PageNavMenu aria-label={t("Navigation_Settings")}>
            <PageNavMenuSection label={t("Settings_YourAccount")}>
                <PageNavMenuItem
                    label={sectionLabel("profile")}
                    to={WebRoute.PROFILE}
                    active={section === "profile"}
                />
            </PageNavMenuSection>
            <PageNavMenuSection label={t("Settings_YourOrganization")}>
                <PageNavMenuItem
                    label={sectionLabel("tags")}
                    to={WebRoute.TAGS}
                    active={section === "tags"}
                />
                {controlEntitiesEnabled && (
                    <PageNavMenuItem
                        label={sectionLabel("entities")}
                        to={controlEntitiesListUrl}
                        active={section === "entities"}
                    />
                )}
                <PageNavMenuItem
                    label={sectionLabel("eventLog")}
                    to={WebRoute.EVENT_LOG}
                    active={section === "eventLog"}
                />
            </PageNavMenuSection>
            {orgSettingsEnabled && canManageControlPolicies && (
                <PageNavMenuSection label={t("Settings_Configuration")}>
                    <PageNavMenuItem
                        label={sectionLabel("controlPolicies")}
                        to={WebRoute.CONTROL_POLICIES}
                        active={section === "controlPolicies"}
                    />
                </PageNavMenuSection>
            )}
        </PageNavMenu>
    );
}

function sectionLabel(section: SettingsSection): string {
    switch (section) {
        case "profile":
            return t("Menu_MyProfile");
        case "tags":
            return t("TagManagement_MenuTitle");
        case "entities":
            return t("Entities_MenuTitle");
        case "eventLog":
            return t("Navigation_AuditTrail");
        case "controlPolicies":
            return t("ControlPolicies_Title");
        default:
            return assertNever(section);
    }
}
